package com.ives.modules.mpr;

import js.html.TouchEvent;
import js.html.WheelEvent;
import js.html.MouseEvent;

import com.ives.utils.math.Vector2D;
import com.ives.utils.math.Quaternion;
import com.ives.utils.math.Vector3D;
import com.ives.components.workspace.DicomWorkspace;
import com.ives.components.viewer.DicomViewer;
import com.ives.utils.Config;
import com.ives.utils.Log;

class MPR extends Module{
	static private inline var MPR_MODULE:String = "MPR_MODULE";
	static private inline var MPR_WORKSPACE_XY:String = "MPR_WORKSPACE_XY";
	static private inline var MPR_WORKSPACE_YZ:String = "MPR_WORKSPACE_YZ";
	static private inline var MPR_WORKSPACE_XZ:String = "MPR_WORKSPACE_XZ";
	static public inline var MPR_WORKSPACE_H:String = "MPR_WORKSPACE_H";
	static private inline var MPR_EVENT:String = "MPR_EVENT";
	static private inline var RESET_EVENT:String = "RESET_3D";

	static private inline var DRAWING_X_ID:String = "ALWAYS_DRAW_x";
	static private inline var DRAWING_Y_ID:String = "ALWAYS_DRAW_y";
	static private inline var DRAWING_Z_ID:String = "ALWAYS_DRAW_z";

	static private inline var DRAWING_XY_COLOR:String = "#ff0000";
	static private inline var DRAWING_XZ_COLOR:String = "#00ff00";
	static private inline var DRAWING_YZ_COLOR:String = "#0000ff";

	private var center:Vector3D = new Vector3D(-1, -1, -1);
	private var mprPosition:Vector3D = new Vector3D(-1, -1, -1);
	private var spacing:Float;
	private var touchStartX:Float;
	private var touchStartY:Float;
	
	private var isDown:Bool = false;

	private var sphereRadius:Float;

	private var orientationXYRow:Vector3D;
	private var orientationXYCol:Vector3D;
	private var orientationXYDep:Vector3D;

	private var orientationXZRow:Vector3D;
	private var orientationXZCol:Vector3D;
	private var orientationXZDep:Vector3D;

	private var orientationYZRow:Vector3D;
	private var orientationYZCol:Vector3D;
	private var orientationYZDep:Vector3D;

	private var rotationData:Array<Float>;
	private var rotationStartPoint:Vector3D = new Vector3D();

	override public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		super.init(dicomViewer, dicomWorkspace);

		if(!(getWorkspaceMode() == MPR_WORKSPACE_XY || getWorkspaceMode() == MPR_WORKSPACE_YZ || getWorkspaceMode() == MPR_WORKSPACE_XZ || getWorkspaceMode() == MPR_WORKSPACE_H))
			return;

		dicomWorkspace.on("mousedown", onMouseDown);
		dicomWorkspace.on("wheel", onMouseWheel);

		dicomWorkspace.on("touchstart", onTouchStart);
		dicomWorkspace.on("touchmove", onTouchMove);

		dicomWorkspace.on("mousemove", onMouseMove);
		dicomWorkspace.on("mouseup", onMouseUp);
		dicomWorkspace.on("mouseout", onMouseOut);

		mprInfo.style.display = "block";

		if(getWorkspaceMode() == MPR_WORKSPACE_XY){
			mprInfo.innerHTML = "X / Y";
			mprInfo.style.color = DRAWING_XY_COLOR;
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_YZ){
			mprInfo.innerHTML = "Y / Z";
			mprInfo.style.color = DRAWING_YZ_COLOR;
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_XZ){
			mprInfo.innerHTML = "X / Z";
			mprInfo.style.color = DRAWING_XZ_COLOR;
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_H)
			dicomWorkspace.viewElement.style.opacity = "0";
	}

	private function mprInit(){
		Log.info("MPR init...");

		var min = Vector3D.fromArray(dicomWorkspace.dicomContextData.min);
		var max = Vector3D.fromArray(dicomWorkspace.dicomContextData.max);
		var dimnesions = max.clone().subtract(min);
		var hd = dimnesions.clone().scale(0.5);

		sphereRadius = Math.max(hd.x, Math.max(hd.y, hd.z)); //Math.sqrt(hd.x * hd.x + hd.y * hd.y + hd.z * hd.z);
		mprPosition = center = min.clone().add(dimnesions.clone().scale(0.5));
		spacing = dicomWorkspace.dicomImageData.pixelSpacing[0];

		orientationXYRow = new Vector3D(0, 1, 0);
		orientationXYCol = new Vector3D(1, 0, 0);
		orientationXYDep = new Vector3D(0, 0, 1);

		orientationXZRow = new Vector3D(0, 0, -1);
		orientationXZCol = new Vector3D(1, 0, 0);
		orientationXZDep = new Vector3D(0, 1, 0);

		orientationYZRow = new Vector3D(0, 0, -1);
		orientationYZCol = new Vector3D(0, 1, 0);
		orientationYZDep = new Vector3D(-1, 0, 0);

		dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
	}

	override public function handleModuleEvent(event:ModuleEvent){
		if(event.type == MPR_MODULE){
			if(dicomViewer.workspaces.length == 4 && dicomViewer.workspaces[0].mode == MPR_WORKSPACE_XZ)
				return;

			Log.debug('Initializing...');

			dispatchEvent(new ModuleEvent(DicomViewer.LAYOUT_CHANGE, [2, 2], [MPR_WORKSPACE_XZ, MPR_WORKSPACE_YZ, MPR_WORKSPACE_XY, MPR_WORKSPACE_H]));
		}

		if(event.type == DicomWorkspace.WORKSPACE_READY && (getWorkspaceMode() == MPR_WORKSPACE_XY || getWorkspaceMode() == MPR_WORKSPACE_XZ || getWorkspaceMode() == MPR_WORKSPACE_YZ)){
			mprInit();
		}

		if(event.type == MPR_EVENT){
			Log.debug('Event!');

			var oldPosition = mprPosition.clone();

			var vectors:Array<Vector3D> = floatsToPoints3D(event.points);

			mprPosition = vectors[0];
			orientationXYRow = vectors[1];
			orientationXYCol = vectors[2];
			orientationXYDep = vectors[3];
			orientationXZRow = vectors[4];
			orientationXZCol = vectors[5];
			orientationXZDep = vectors[6];
			orientationYZRow = vectors[7];
			orientationYZCol = vectors[8];
			orientationYZDep = vectors[9];

			if(getWorkspaceMode() == MPR_WORKSPACE_XY){
				getImageForPlane(mprPosition, orientationXYCol, orientationXYRow, orientationXYDep);

				var xLineStart = mprPosition.clone().add(orientationXZCol.clone().scale(20*sphereRadius));
				var xLineStop = mprPosition.clone().subtract(orientationXZCol.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([xLineStart, xLineStop]), DRAWING_X_ID, DRAWING_XZ_COLOR));

				var yLineStart = mprPosition.clone().add(orientationYZCol.clone().scale(20*sphereRadius));
				var yLineStop = mprPosition.clone().subtract(orientationYZCol.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([yLineStart, yLineStop]), DRAWING_Y_ID, DRAWING_YZ_COLOR));
			}
			else if(getWorkspaceMode() == MPR_WORKSPACE_YZ){
				getImageForPlane(mprPosition, orientationYZCol, orientationYZRow, orientationYZDep);

				var yLineStart = mprPosition.clone().add(orientationXYRow.clone().scale(20*sphereRadius));
				var yLineStop = mprPosition.clone().subtract(orientationXYRow.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([yLineStart, yLineStop]), DRAWING_Y_ID, DRAWING_XY_COLOR));

				var zLineStart = mprPosition.clone().add(orientationXZRow.clone().scale(20*sphereRadius));
				var zLineStop = mprPosition.clone().subtract(orientationXZRow.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([zLineStart, zLineStop]), DRAWING_Z_ID, DRAWING_XZ_COLOR));
			}
			else if(getWorkspaceMode() == MPR_WORKSPACE_XZ){
				getImageForPlane(mprPosition, orientationXZCol, orientationXZRow, orientationXZDep);

				var xLineStart = mprPosition.clone().add(orientationXYCol.clone().scale(20*sphereRadius));
				var xLineStop = mprPosition.clone().subtract(orientationXYCol.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([xLineStart, xLineStop]), DRAWING_X_ID, DRAWING_XY_COLOR));

				var zLineStart = mprPosition.clone().add(orientationYZRow.clone().scale(20*sphereRadius));
				var zLineStop = mprPosition.clone().subtract(orientationYZRow.clone().scale(20*sphereRadius));

				addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D([zLineStart, zLineStop]), DRAWING_Z_ID, DRAWING_YZ_COLOR));
			}
		}

		if(event.type == DicomViewer.RESIZED)
			redraw();

		if(event.type == RESET_EVENT)
			mprInit();
	}

	function eventData():Array<Float>{
		return pointsToFloats3D([
			mprPosition,
			orientationXYRow,
			orientationXYCol,
			orientationXYDep,
			orientationXZRow,
			orientationXZCol,
			orientationXZDep,
			orientationYZRow,
			orientationYZCol,
			orientationYZDep
		]);
	}

	private function getImageForPlane(mprPosition:Vector3D, columnDirection:Vector3D, rowDirection:Vector3D, planeNormal:Vector3D){
		if(Math.isNaN(sphereRadius))
			return;

		var position = center.clone()
		.subtract(rowDirection.clone().scale(sphereRadius)) // move to left edge
		.subtract(columnDirection.clone().scale(sphereRadius)) // move to top edge
		.add(planeNormal.clone().scale(mprPosition.clone().subtract(center).dot(planeNormal))); // move to correct depth
		var screen:Vector2D = new Vector2D(2*sphereRadius, 2*sphereRadius);

		dicomWorkspace.dicomImage.setBaseUrl('${Config.SERVER_URL}/dicom/${dicomViewer.originalDicomImageData.sourceName}/context/${dicomViewer.originalDicomImageData.id}/slice?position=${position}&columnDirection=${columnDirection}&rowDirection=${rowDirection}&screen=${screen}&spacing=${spacing}&mpr=true');
	}

	private function onTouchStart(event:TouchEvent){
		event.preventDefault();

		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		touchStartX = event.touches.item(0).pageX;
		touchStartY = event.touches.item(0).pageY;

		if(event.touches.length == 1)
			onDown(touchToVolume(event));
	}

	private function onMouseDown(event:MouseEvent){
		if(hasModifier(event))
			return;

		onDown(mouseToVolume(event));
	}

	private function onDown(volumePoint:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		isDown = true;
		rotationData = null;

		var local = volumeToLocal([volumePoint, mprPosition]);
		var localMouse = local[0];
		var localMprPosition = local[1];

		var planeDistanceXY = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationXYDep));
		var planeDistanceXZ = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationXZDep));
		var planeDistanceYZ = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationYZDep));

		var planeDistanceInScreenPxXY = (planeDistanceXY / spacing) * dicomWorkspace.dicomImage.scale;
		var planeDistanceInScreenPxXZ = (planeDistanceXZ / spacing) * dicomWorkspace.dicomImage.scale;
		var planeDistanceInScreenPxYZ = (planeDistanceYZ / spacing) * dicomWorkspace.dicomImage.scale;

		var planeDistance = 32;
		var grabDistance = Math.min(dicomWorkspace.viewElement.clientWidth, dicomWorkspace.viewElement.clientHeight) / 4;

		if(localMouse.subtract(localMprPosition).length2() < grabDistance*grabDistance){
			mprPosition = volumePoint;

			dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
		}
		else if((getWorkspaceMode() == MPR_WORKSPACE_XY && (planeDistanceInScreenPxXZ < planeDistance || planeDistanceInScreenPxYZ < planeDistance)) ||
			(getWorkspaceMode() == MPR_WORKSPACE_XZ && (planeDistanceInScreenPxXY < planeDistance || planeDistanceInScreenPxYZ < planeDistance)) ||
			(getWorkspaceMode() == MPR_WORKSPACE_YZ && (planeDistanceInScreenPxXY < planeDistance || planeDistanceInScreenPxXZ < planeDistance))){
			Log.info('Rotation start (${getWorkspaceMode()}})');

			rotationData = eventData();
			rotationStartPoint = volumePoint;
		}
		else{
			mprPosition = volumePoint;

			dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
		}
	}

	private function onTouchMove(event:TouchEvent){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(event.touches.length == 4)
			onWheel(event.touches.item(0).pageX - touchStartX);
	}

	private function onMouseWheel(event:WheelEvent){
		onWheel(event.deltaY/10);
	}

	private function onWheel(delta:Float){
		if(getWorkspaceMode() == MPR_WORKSPACE_XY){
			mprPosition.add(orientationXYDep.clone().scale(delta > 0 ? 1 : -1));

			dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_YZ){
			mprPosition.add(orientationYZDep.clone().scale(delta > 0 ? 1 : -1));

			dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_XZ){
			mprPosition.add(orientationXZDep.clone().scale(delta > 0 ? 1 : -1));

			dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
		}
	}

	private function onMouseMove(event:MouseEvent){
		if(getWorkspaceMode() == MPR_WORKSPACE_H || hasModifier(event))
			return;

		onMove(mouseToVolume(event));
	}

	private function onMove(volumePoint:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked()){
			setCursor(null);

			return;
		}

		var local = volumeToLocal([volumePoint, mprPosition, rotationStartPoint]);
		var localMouse = local[0];
		var localMprPosition = local[1];
		var localRotationPoint = local[2];

		var planeDistanceXY = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationXYDep));
		var planeDistanceXZ = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationXZDep));
		var planeDistanceYZ = Math.abs(volumePoint.clone().subtract(mprPosition).dot(orientationYZDep));

		var planeDistanceInScreenPxXY = (planeDistanceXY / spacing) * dicomWorkspace.dicomImage.scale;
		var planeDistanceInScreenPxXZ = (planeDistanceXZ / spacing) * dicomWorkspace.dicomImage.scale;
		var planeDistanceInScreenPxYZ = (planeDistanceYZ / spacing) * dicomWorkspace.dicomImage.scale;

		var grabDistance = Math.min(dicomWorkspace.viewElement.clientWidth, dicomWorkspace.viewElement.clientHeight) / 4;
		var planeDistance = 32;

		if(localMouse.subtract(localMprPosition).length2() < grabDistance*grabDistance){
			setCursor("mpr-move");
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_XY && (planeDistanceInScreenPxXZ < planeDistance || planeDistanceInScreenPxYZ < planeDistance)){
			setCursor("mpr-rotate");
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_YZ && (planeDistanceInScreenPxXY < planeDistance || planeDistanceInScreenPxXZ < planeDistance)){
			setCursor("mpr-rotate");
		}
		else if(getWorkspaceMode() == MPR_WORKSPACE_XZ && (planeDistanceInScreenPxXY < planeDistance || planeDistanceInScreenPxYZ < planeDistance)){
			setCursor("mpr-rotate");
		}
		else{
			setCursor("mpr-target");
		}

		if(isDown){
			if(localMouse.subtract(localMprPosition).length2() < grabDistance*grabDistance){
				mprPosition = volumePoint;

				dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
			}

			if(rotationData != null){
				var vectors:Array<Vector3D> = floatsToPoints3D(rotationData);

				mprPosition = vectors[0];
				orientationXYRow = vectors[1];
				orientationXYCol = vectors[2];
				orientationXYDep = vectors[3];
				orientationXZRow = vectors[4];
				orientationXZCol = vectors[5];
				orientationXZDep = vectors[6];
				orientationYZRow = vectors[7];
				orientationYZCol = vectors[8];
				orientationYZDep = vectors[9];

				var a = localRotationPoint.clone().subtract(localMprPosition);
				var b = localMouse.clone().subtract(localMprPosition);

				var radians = Math.atan2(b.y, b.x) - Math.atan2(a.y, a.x);

				if(getWorkspaceMode() == MPR_WORKSPACE_XY){
					var rotation:Quaternion = Quaternion.fromAxisAngle(orientationXYDep, radians); // create the rotation

					rotation.rotate(orientationXZRow);
					rotation.rotate(orientationXZCol);
					rotation.rotate(orientationXZDep);
					rotation.rotate(orientationYZRow);
					rotation.rotate(orientationYZCol);
					rotation.rotate(orientationYZDep);
				}
				else if(getWorkspaceMode() == MPR_WORKSPACE_YZ){
					var rotation:Quaternion = Quaternion.fromAxisAngle(orientationYZDep, radians); // create the rotation

					rotation.rotate(orientationXYRow);
					rotation.rotate(orientationXYCol);
					rotation.rotate(orientationXYDep);
					rotation.rotate(orientationXZRow);
					rotation.rotate(orientationXZCol);
					rotation.rotate(orientationXZDep);
				}
				else if(getWorkspaceMode() == MPR_WORKSPACE_XZ){
					var rotation:Quaternion = Quaternion.fromAxisAngle(orientationXZDep, radians); // create the rotation

					rotation.rotate(orientationXYRow);
					rotation.rotate(orientationXYCol);
					rotation.rotate(orientationXYDep);
					rotation.rotate(orientationYZRow);
					rotation.rotate(orientationYZCol);
					rotation.rotate(orientationYZDep);
				}

				dispatchEvent(new ModuleEvent(MPR_EVENT, eventData(), false, true));
			}
		}
	}

	private function setCursor(value:String){
		dicomWorkspace.viewElement.classList.remove("mpr-move");
		dicomWorkspace.viewElement.classList.remove("mpr-target");
		dicomWorkspace.viewElement.classList.remove("mpr-rotate");

		if(value != null)
			dicomWorkspace.viewElement.classList.add(value);
	}

	private function onMouseUp(event:MouseEvent){
		if(hasModifier(event))
			return;

		onUp(mouseToVolume(event));
	}

	private function onMouseOut(event:MouseEvent){
		isDown = false;
	}

	private function onUp(image:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		isDown = false;
	}
}
